import { memo, useId, type ReactNode } from 'react'
import type { AgentRow } from '../transport/api'
import { PixelPet } from '../identity/PixelPet'
import { Icon } from '../ui/Icon'
import { agentColor } from '../identity/agentColor'
import { productForKind, kindSlug } from '../agents/agentKinds'
import { presenceFor, askingVerdict, type useIdleStatus } from '../agents/useIdleStatus'
import { Avatar } from '../chat/Avatar'
import { blockedKey, blockedNotice, type BlockedReason } from '../agents/blockedAgents'
import { machineNameOf } from '../agents/machineName'
import { idleKey } from '../agents/everywhere'
import { pinKey } from './usePins'

/**
 * EVERYTHING A ROW KNOWS, as one typed object.
 *
 * The row used to be a 280-line closure inside Sidebar, and its comment defended that:
 * both call sites (the machine sections and the Pinned section) MUST agree on every
 * helper — presence, preview, pending notes, queued mail, the stuck notice, the pin
 * state — and fifteen props would be "a second, drifting list of what a row knows".
 * The premise holds; the conclusion didn't. THIS type is the list, the Sidebar builds
 * ONE object against it, and both call sites receive the same object — the same
 * cannot-drift guarantee, now written down where tsc reads it, plus a row that can be
 * rendered alone without mounting the whole rail. (M6 拆面板, 2026-09-03.)
 */
export type AgentRowCtx = {
  idle: ReturnType<typeof useIdleStatus>
  openAgentHash: string | null
  openAgentDevice: string | null
  onDragSel:
    | ((sel: { kind: 'agent'; session: string; host: string | null; hash: string; device: string | null } | null) => void)
    | null
  onOpenAgent: (a: AgentRow) => void
  onOpenCard: ((a: AgentRow) => void) | null
  pendingFor: (a: AgentRow) => number
  queuedFor: (a: AgentRow) => number
  blocked: Map<string, BlockedReason>
  localName: string | null
  q: string
  hit: (...fields: (string | null | undefined)[]) => boolean
  contentHits: Set<string> | null
  filter: string
  anyPreview: boolean
  /** The second line, and whether it quotes THIS ACCOUNT rather than the agent. */
  previewFor: (a: AgentRow) => { text: string; fromMe: boolean }
  /** The account's own face, drawn beside its own words. Null while signed out or
   *  before /auth/me answers — the line still renders, just without the face. */
  myFace: { picture: string | null; email: string } | null
  isPinned: (a: AgentRow) => boolean
  /** The pin was pressed: open the sheet that says which tag (owner, 2026-10-12). */
  onPin: (a: AgentRow) => void
}

/**
 * Memoised: a row draws again when its agent, its lead or the rail's context changes — and the rail builds that context
 * to change exactly when something a row shows does (Sidebar's rowCtx). Owner, 2026-11-04: UI 有時候會變得很卡; every
 * render of the app used to draw every row and its pet again.
 */
export const AgentRowView = memo(function AgentRowView({ ctx, a, lead }: { ctx: AgentRowCtx; a: AgentRow; lead?: ReactNode }) {
  const pres = presenceFor(ctx.idle.byKey, a)
  /*
   * THE ROW IS NAMED BY ITS AGENT. Left to its content, a treeitem is named by every word
   * inside it — the face button's label first — so it was announced "Settings for X …"
   * (and "About X …" before that) when what it opens is the session. Its kind, notes
   * and last line are the description.
   */
  const ids = useId()
  const nameId = `${ids}-name`
  const tagsId = `${ids}-tags`
  const lastId = `${ids}-last`
    return (
    <div
      key={pinKey(a.device_id, a.hash)}
      title={pres === 'working' ? `${a.session} — working…` : a.session}
      className={`sb-row${ctx.openAgentHash === a.hash && (ctx.openAgentDevice ?? null) === (a.device_id ?? null) ? ' active' : ''}`}
      /*
       * DRAG A CONVERSATION ONTO THE STAGE — owner, 2026-08-22: 從 Side Panel
       * 將一個元件拖到主顯示欄位並選擇位置, 造成雙開或多開.
       *
       * `draggable` only where the shell can actually accept it: the parent
       * decides, because a pane iframe renders no rail and a phone has nowhere
       * to drop. A row that lifts and cannot land is worse than one that does
       * not lift.
       */
      draggable={!!ctx.onDragSel}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move'
        // Some text, or Safari refuses to start the drag at all.
        e.dataTransfer.setData('text/plain', a.session)
        ctx.onDragSel?.({
          kind: 'agent',
          session: a.session,
          host: a.host ?? null,
          hash: a.hash,
          device: a.device_id ?? null,
        })
      }}
      onDragEnd={() => ctx.onDragSel?.(null)}
      role="treeitem"
      aria-labelledby={nameId}
      aria-describedby={`${tagsId} ${lastId}`}
      aria-selected={ctx.openAgentHash === a.hash && (ctx.openAgentDevice ?? null) === (a.device_id ?? null)}
      tabIndex={0}
      onClick={() => ctx.onOpenAgent(a)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          ctx.onOpenAgent(a)
        }
      }}
      style={{ boxShadow: `inset 2px 0 0 ${agentColor(a.hash)}` }}
    >
      {lead}
      {/* Working = the same spinning green halo as the project chat's busy
          float, round the face. */}
      {/* The face opens the agent's card. A BUTTON, not a click handler on
          the span: it is a control now, so it needs to be reachable by keyboard
          and to announce itself — and stopPropagation, because the row behind
          it opens the terminal and the two must not both fire. */}
      <button
        type="button"
        className={`sb-pet sb-pet-btn${pres === 'working' ? ' busy' : ''}`}
        title={`Settings for ${a.session}`}
        aria-label={`Settings for ${a.session}`}
        aria-haspopup="dialog"
        onClick={(e) => {
          e.stopPropagation()
          ctx.onOpenCard?.(a)
        }}
        /*
         * Enter and Space are the button's own: the row answers them by opening the
         * session and preventDefault()s them, which cancelled this button's activation —
         * from a keyboard the face opened the session and never the card. The pin
         * button below stops them for the same reason.
         */
        onKeyDown={(e) => e.stopPropagation()}
      >
        <PixelPet name={a.session} seed={a.hash} size={26} active={pres === 'working'} />
        {/*
         * A GEAR WHERE THE GREY DOT WAS — owner, 2026-10-14: Side panel 的右下角, 現在是個
         * 灰色的圓點. 我想把這個改為齒輪 icon. 這樣使用者就可以知道來點擊這個 icon 來調整
         * Agent 的設定.
         *
         * The dot was presence: grey for idle, nothing for off, and hidden while working
         * (the halo says that). The gear says what pressing the face does, so it is drawn
         * in EVERY state — a way in that comes and goes with the agent's activity is one
         * nobody learns. Idle no longer has a mark of its own; working keeps its halo.
         *
         * Drawn, not a second button: the whole face stays the target (26px is already
         * small for a thumb), and the gear is how it says so.
         */}
        <span className="sb-gear" aria-hidden="true">
          <Icon name="settings" size={11} />
        </span>
        {/* Unread sits on the face, top-right — the gear has the bottom-right
            corner, so the two never overlap. On the avatar it is attached to
            WHO is unread, which a badge at the row's far end only implies by
            alignment. */}
        {(() => {
          const unread = ctx.idle.byKey.get(idleKey(a))?.unread ?? 0
          if (unread <= 0) return null
          return (
            <span
              className="sb-unread"
              title={`${unread} unread message${unread === 1 ? '' : 's'}`}
            >
              {/* Measured: "99+" renders 29px wide over a 26px avatar —
                  the badge eclipses the face it is meant to mark. Two
                  characters is what this corner can carry, and past nine
                  the exact count stops changing what anyone does. The
                  title attribute keeps the real number. */}
              {unread > 9 ? '9+' : unread}
            </span>
          )
        })()}
      </button>
      <span className="sb-meta">
        <span className="sb-nameline">
          <span className="sb-name" id={nameId}>{a.session}</span>
          {/* Type, server and queued notes on one right-aligned group.
           *
           * They were three separate places before — the kind beside the
           * name, the host and the note badge as siblings of the whole meta
           * block — so the row had four things competing for the width the
           * NAME needs. Grouped, they cost one measurement instead of three
           * and the name gets everything they do not use.
           *
           * The kind comes from the ctx.idle poll's `kind`, the same field
           * auto-detect reads, so the rail and the open view can never
           * disagree about one agent. Never inferred from the session name:
           * `antigravity-agent` contains nothing load-bearing, and a guess
           * here would be a second opinion that drifts from the server's in
           * silence. An unknown kind renders nothing rather than a
           * placeholder — a CLI shipped after this build is a real state. */}
          {(() => {
            /*
             * Live row first, last-known second. A remote that missed this
             * tick contributes nothing, and without the fallback the badge
             * emptied and refilled every few seconds — the agent had not
             * changed, only the poll's luck had.
             *
             * `held` is display-only by construction; the working halo on
             * the face still reads the live map, so an unreachable agent
             * keeps its name and loses its liveness, which is the honest
             * split.
             */
            const k = idleKey(a)
            const kind = ctx.idle.byKey.get(k)?.kind ?? ctx.idle.held.get(k)?.kind
            const product = productForKind(kind)
            const slug = kindSlug(kind)
            /*
             * THE PRODUCT ALONE. The machine's name is the SECTION HEADING
             * these rows sit under — `<Section label={nameOf(host)}>` a few
             * lines up — so "Claude @ aowl" repeated it on every row inside a
             * block already titled "aowl".
             *
             * The rule it replaces appended the host whenever the account had
             * more than one machine, and that was right when the rail was one
             * flat list. Grouping by machine is what made it redundant: the
             * condition it tested ("could this be ambiguous?") stopped being
             * the question the moment the answer was in the heading. A rule
             * that outlives the layout it was measured against reads as
             * information and is noise — and on a 208px rail it is noise that
             * costs the width the agent's own name needed.
             */
            const label = product
            const notes = ctx.pendingFor(a)
            const queued = ctx.queuedFor(a)
            /*
             * STUCK WAITING FOR A PERSON — the state that used to be invisible.
             *
             * An agent sitting on a prompt is alive and unreachable: every
             * message to it is held, and from the outside it reads as "it is
             * ignoring me". Nothing on this rail said so, and finding it took
             * four of us hours with the source in front of us. It goes in the
             * SAME pill as the other two because it answers the same question
             * they do — what is going on with this agent — and a separate
             * indicator would compete with the one that matters.
             */
            /*
             * THE SWITCH OUTRANKS THE CLOUD'S MEMORY — owner, 2026-08-30: 我已經
             * 回覆了 … 的問題, 但系統還是顯示他有提問. The cloud's `ctx.blocked`
             * refreshes on link traffic; the edge's asking switch refreshes on
             * the pane's own flips. Where the edge has spoken, its verdict is
             * the truth in both directions; the cloud field survives only as
             * the fallback for a pre-switch edge.
             */
            const asked = askingVerdict(a.device_id, a.session)
            const stuck =
              asked === false
                ? null
                : blockedNotice(
                    asked === true
                      ? 'at_prompt'
                      : ctx.blocked.get(blockedKey(a.device_id, a.session)),
                    machineNameOf(a, ctx.localName),
                  )
            if (!label && notes <= 0 && queued <= 0 && !stuck) return null
            return (
              <span
                id={tagsId}
                className={`sb-tags sb-kind${slug ? ` k-${slug}` : ''}`}
                title={
                  [
                    product ? `running ${product}` : null,
                    notes > 0 ? `${notes} note(s) queued` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || undefined
                }
              >
                {label}
                {/* Queued notes sit INSIDE the type pill, at the owner's
                    request — one object rather than two with a rule between
                    them.

                    They keep their own colour, which is the part that has to
                    survive the merge. The count means "you (or another agent)
                    left work here", and the unread badge on the avatar means
                    "this agent said something to you"; letting it take the
                    pill's colour would make it read as one more word about
                    which CLI this is. */}
                {notes > 0 && (
                  <span className="sb-notes">
                    <Icon name="edit" size={11} />
                    {notes}
                  </span>
                )}
                {/* Messages HELD for this agent — deliberately distinct from
                    queued notes beside it. A note is work someone left; this
                    is mail that could not be delivered because the agent is
                    busy, and it moves on its own once it can. Same pill, own
                    colour, because they answer different questions. */}
                {queued > 0 && (
                  <span className="sb-queued" title={`${queued} message(s) waiting to be delivered`}>
                    <Icon name="history" size={11} />
                    {queued}
                  </span>
                )}
                {/* Its own colour, and a word rather than a count: there is
                    nothing to count, and what a person needs is the verb.
                    The full sentence — including WHICH machine to go to — is
                    in the title, because the rail is 208px and the machine
                    name is the part that does not fit. */}
                {stuck && (
                  <span className="sb-stuck" title={stuck.long}>
                    <Icon name="pause" size={11} />
                    {stuck.short}
                  </span>
                )}
              </span>
            )
          })()}
        </span>
        {/* Says WHY this row is here when the name has nothing to do with
            the query. Without it a content ctx.hit reads as a broken ctx.filter —
            an unrelated agent appearing for no visible reason. Only when
            the name did NOT match, so it never states the obvious. */}
        {ctx.q && !ctx.hit(a.session, a.host) && ctx.contentHits?.has(a.hash) && (
          <span className="sb-why">said “{ctx.filter.trim()}”</span>
        )}
        {/* The opening of this agent's last message.
         *
         * The row reserves the line whether or not there is text, so every
         * row is the same height — a list that changes shape as messages
         * arrive is harder to scan than one with an occasional blank line.
         * &nbsp; rather than nothing, so the empty line still occupies its
         * own height instead of collapsing.
         *
         * Truncation is CSS, not a slice: the server sends ~80 characters
         * and the ellipsis lands wherever the column actually ends, which
         * differs between a phone rail and a dragged-wide desktop one. */}
        {ctx.anyPreview && (() => {
          /* Whose words these are decides whether a face is drawn: the agent's own
             line has its pet two columns to the left already, and a second face
             there would say nothing. The account's own words carry the account's
             own face (owner, 2026-09-13), so a glance says "this is what I asked
             it", not "this is what it answered". */
          const p = ctx.previewFor(a)
          return (
            <span id={lastId} className={`sb-last${p.fromMe ? ' mine' : ''}`}>
              {p.fromMe && ctx.myFace && (
                <Avatar
                  picture={ctx.myFace.picture}
                  label={ctx.myFace.email}
                  email={ctx.myFace.email}
                  className="sb-ask-face"
                />
              )}
              {p.text || '\u00a0'}
            </span>
          )
        })()}
      </span>
      {/* PIN — owner, 2026-08-24: 每一個 Agent 的右邊增加一個小按鈕.
       *
       * Quiet until hovered, lit when this agent is pinned, and the SAME control on
       * the row's copy in the pinned section — so there is one meaning for the glyph
       * rather than a "pin" here and a "remove" there.
       *
       * IT OPENS THE SHEET — owner, 2026-10-12: 當使用者按下 Pin 的這個按鈕的時候, 就會
       * 彈出一個視窗, Default 就是 Pinned 的那一個 Tag. Which tag is the question a pin
       * now asks, so pressing it no longer pins or unpins by itself: the sheet names
       * the tags, the one this agent is under, and pressing that one again unpins.
       *
       * stopPropagation for the reason the pet button has it: the row behind opens
       * the terminal. On keydown as well — the row answers Enter/Space by opening
       * the agent, and an Enter bubbling up from here would do that AND this. */}
      <button
        type="button"
        className={`sb-pin${ctx.isPinned(a) ? ' on' : ''}`}
        title={ctx.isPinned(a) ? `${a.session} is pinned — move it to another tag, or unpin` : `Pin ${a.session} to the top of the rail`}
        aria-label={ctx.isPinned(a) ? `Change the pin on ${a.session}` : `Pin ${a.session}`}
        aria-pressed={ctx.isPinned(a)}
        aria-haspopup="dialog"
        onClick={(e) => {
          e.stopPropagation()
          ctx.onPin(a)
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Icon name="push_pin" size={14} />
      </button>
      {/* Notes and host used to render here, as siblings of the meta
          block. Both now sit in the name line's tag group above — see there
          for why.

          THE TRASH CAN IS GONE FROM THIS ROW, at the owner's request. A
          destructive control lived one tap from "open this agent", on a 208px
          rail, permanently — and its confirmation was the word "end?" inside a
          13px button, which names nothing. It moved onto the pet: see
          AgentCard, which has room to say which session on which machine, in
          which directory, with what still queued against it. */}
    </div>
    )
})
